import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable max-lines-per-function, max-lines */
import { useCallback, useState } from "react";
import { EllipsisText, Flex, FlexSpan, GrayButton, H4, Icon, NumberInput, TextInput, Tooltip } from "../../../../ui";
import { ParamsTable } from "../../../../components/ParamsTable";
import { useBusinessPlanFormValues } from "../../../../hooks";
import { formatNumber, onParamsTableMonthClick } from "../../../../utils";
const INITIAL_FINANCING_PARAMS = [
    {
        id: "reg_cost",
        schedule: "reg_schedule",
        title: "Регистрация в качестве самозанятого",
    },
    {
        id: "docs_cost",
        schedule: "docs_schedule",
        title: "Оформление необходимых для ведения бизнеса документов",
    },
    {
        id: "deposit_cost",
        schedule: "deposit_schedule",
        title: "Подбор помещения и заключение договора аренды помещения",
    },
    {
        id: "repair_cost",
        schedule: "repair_schedule",
        title: "Подготовка и ремонт помещения",
    },
    {
        id: "product_cost",
        schedule: "product_schedule",
        title: "Закупка первой партии расходных материалов",
    },
    {
        id: "soft_cost",
        schedule: "soft_schedule",
        title: "Приобретение лицензий на необходимое программное обеспечение",
    },
    {
        id: "adv_cost",
        schedule: "adv_schedule",
        title: "Проведение рекламной кампании",
    },
];
export const InitialFinancingForm = ({ data, defaultParams, keys, shouldReset, onResetComplete, onChange, onValuesChange }) => {
    const [editingField, setEditingField] = useState();
    const [editingEquipmentField, setEditingEquipmentField] = useState();
    const [values, onUpdateValues] = useBusinessPlanFormValues(keys.reduce((acc, curr) => ({ ...acc, [curr]: data?.parameters[curr] }), {}), {
        reg_cost: defaultParams.reg_cost?.value,
        reg_schedule: defaultParams.reg_schedule,
        docs_cost: defaultParams.docs_cost?.value,
        docs_schedule: defaultParams.docs_schedule,
        deposit_cost: defaultParams.deposit_cost?.value,
        deposit_schedule: defaultParams.deposit_schedule,
        repair_cost: defaultParams.repair_cost?.value,
        repair_schedule: defaultParams.repair_schedule,
        product_cost: defaultParams.product_cost?.value,
        product_schedule: defaultParams.product_schedule,
        soft_cost: defaultParams.soft_cost?.value,
        soft_schedule: defaultParams.soft_schedule,
        adv_cost: defaultParams.adv_cost?.value,
        adv_schedule: defaultParams.adv_schedule,
        equipment_list: defaultParams.equipment_list.map(item => ({
            ...item,
            count: item.count.value,
            price: item.price.value,
            time: item.time.value,
        })),
    }, onChange, shouldReset, onResetComplete, onValuesChange);
    const onChangeEquipmentCellValue = useCallback((rowId, key, value) => {
        onUpdateValues({
            equipment_list: values.equipment_list.map((currRow, currIndex) => {
                if (currIndex === rowId) {
                    return {
                        ...currRow,
                        [key]: value,
                    };
                }
                return currRow;
            }),
        });
    }, [onUpdateValues, values.equipment_list]);
    const onAddEquipment = useCallback(() => {
        setEditingField(undefined);
        setEditingEquipmentField(undefined);
        onUpdateValues({
            equipment_list: [
                ...values.equipment_list,
                {
                    id: -1 * (values.equipment_list.filter(item => item.id < 0).length + 1),
                    name: "Доп. оборудование",
                    count: defaultParams.equipment_list[0].count?.min,
                    price: defaultParams.equipment_list[0].price?.min,
                    schedule: Array(12).fill(0),
                    time: defaultParams.equipment_list[0].time?.min,
                },
            ],
        });
    }, [defaultParams, onUpdateValues, values]);
    const onPickMenuItem = useCallback((option, rowId) => {
        if (option.value === 1) {
            let equipmentIdCount = 0;
            onUpdateValues({
                equipment_list: values.equipment_list.reduce((acc, curr, currIndex) => {
                    return currIndex === rowId ? acc : [...acc, curr];
                }, []).map(item => {
                    if (item.id < 0) {
                        equipmentIdCount += 1;
                        return {
                            ...item,
                            id: -1 * equipmentIdCount,
                        };
                    }
                    return item;
                }),
            });
        }
    }, [onUpdateValues, values]);
    return (_jsxs(Flex, { column: true, children: [_jsxs(FlexSpan, { width: "100%", mb: "2rem", children: [_jsx(H4, { fontWeight: 500, mb: "1.5rem", children: "\u041F\u0440\u0435\u0434\u0432\u0430\u0440\u0438\u0442\u0435\u043B\u044C\u043D\u044B\u0435 \u0437\u0430\u0442\u0440\u0430\u0442\u044B" }), _jsx(ParamsTable, { startDate: data.parameters.start_date, monthsCount: 12, columns: [
                            {
                                id: "name",
                                title: "Статья инвестиций",
                                width: "14.25rem",
                            },
                            {
                                id: "cost",
                                title: "Стоимость",
                                subtitle: "руб.",
                                width: "7.625rem",
                                align: "right",
                                onEditCheck: rowId => editingField !== undefined && editingField[0] === rowId && editingField[1] === 1,
                                formatter: (row, rowId) => {
                                    return editingField !== undefined && editingField[0] === rowId && editingField[1] === 1 ? (_jsx(NumberInput, { autofocus: true, style: { border: "none" }, inputStyle: { textAlign: "right", fontSize: "0.75rem", padding: "0 0.75rem" }, defaultValue: row.data.cost?.toString(), hideArrows: true, height: "2.5rem", onChange: value => onUpdateValues({ [row.id]: value }), min: defaultParams[row.id].min, max: defaultParams[row.id].max, onEnter: () => setEditingField(undefined) })) : formatNumber(row.data.cost, false);
                                },
                            },
                        ], rows: INITIAL_FINANCING_PARAMS.map((param, paramIndex) => {
                            return {
                                id: param.id,
                                onColumnClick: colIndex => {
                                    setEditingField(colIndex === 1 ? [paramIndex, colIndex] : undefined);
                                    setEditingEquipmentField(undefined);
                                },
                                onMonthClick: colIndex => onParamsTableMonthClick(colIndex, values[param.schedule], param.schedule, onUpdateValues),
                                onActiveCheck: colIndex => values[param.schedule]?.[colIndex] !== 0 && values[param.schedule]?.[colIndex] !== undefined,
                                onDisabledCheck: colIndex => data.parameters.event_list.find(item => item.type === "power_start").month?.[0] < colIndex + 1,
                                data: {
                                    name: param.title,
                                    cost: values[param.id],
                                    months: values[param.schedule],
                                },
                            };
                        }) })] }), _jsxs(FlexSpan, { width: "100%", children: [_jsx(H4, { fontWeight: 500, mb: "1.5rem", children: "\u041F\u043B\u0430\u043D-\u0433\u0440\u0430\u0444\u0438\u043A \u043F\u0440\u0438\u043E\u0431\u0440\u0435\u0442\u0435\u043D\u0438\u044F \u043E\u0441\u043D\u043E\u0432\u043D\u044B\u0445 \u0441\u0440\u0435\u0434\u0441\u0442\u0432" }), _jsx(ParamsTable, { rowMenuItems: [
                            { value: 1, label: "Удалить строку", prefix: _jsx(Icon, { icon: "trash" }) },
                        ], onPickMenuItem: onPickMenuItem, startDate: data.parameters.start_date, monthsCount: 12, headCellHeight: "6.5rem", columns: [
                            {
                                id: "name",
                                title: "Наименование оборудования",
                                width: "14.25rem",
                                bodyCellStyle: { backgroundColor: "#FFFFFF" },
                                onEditCheck: rowId => editingEquipmentField !== undefined && editingEquipmentField[0] === rowId && editingEquipmentField[1] === 0,
                                formatter: (row, rowId) => {
                                    if (editingEquipmentField !== undefined && editingEquipmentField[0] === rowId && editingEquipmentField[1] === 0) {
                                        return (_jsx(TextInput, { autofocus: true, style: { marginLeft: "0.5rem", border: "none" }, inputStyle: { fontSize: "0.75rem", padding: 0 }, defaultValue: row.data.name, height: "2.5rem", width: "calc(100% - 0.5rem)", onBlur: () => {
                                                if (!values.equipment_list[rowId].name.trim()) {
                                                    onChangeEquipmentCellValue(rowId, "name", "Доп. оборудование");
                                                }
                                            }, onChange: value => onChangeEquipmentCellValue(rowId, "name", value), onEnter: () => setEditingEquipmentField(undefined) }));
                                    }
                                    return (_jsx(Tooltip, { content: row.data.name, delay: [1000, 0], wrapperHeight: "100%", children: _jsx(Flex, { width: "100%", height: "100%", alignItems: "center", p: "0 0.5rem", children: _jsx(FlexSpan, { width: "100%", children: _jsx(EllipsisText, { children: row.data.name }) }) }) }));
                                },
                            },
                            {
                                id: "count",
                                title: "Кол-во",
                                width: "7.625rem",
                                align: "right",
                                onEditCheck: rowId => editingEquipmentField !== undefined && editingEquipmentField[0] === rowId && editingEquipmentField[1] === 1,
                                formatter: (row, rowId) => {
                                    return editingEquipmentField !== undefined && editingEquipmentField[0] === rowId && editingEquipmentField[1] === 1 ? (_jsx(NumberInput, { autofocus: true, style: { border: "none" }, inputStyle: { textAlign: "right", fontSize: "0.75rem", padding: "0 0.75rem" }, defaultValue: row.data.count?.toString(), hideArrows: true, height: "2.5rem", onChange: value => onChangeEquipmentCellValue(rowId, "count", value), min: defaultParams.equipment_list?.[0].count?.min, max: defaultParams.equipment_list?.[0].count?.max, onEnter: () => setEditingEquipmentField(undefined) })) : formatNumber(row.data.count, false);
                                },
                            },
                            {
                                id: "price",
                                title: "Цена за ед.",
                                subtitle: "руб.",
                                width: "8.625rem",
                                align: "right",
                                onEditCheck: rowId => editingEquipmentField !== undefined && editingEquipmentField[0] === rowId && editingEquipmentField[1] === 2,
                                formatter: (row, rowId) => {
                                    return editingEquipmentField !== undefined && editingEquipmentField[0] === rowId && editingEquipmentField[1] === 2 ? (_jsx(NumberInput, { autofocus: true, style: { border: "none" }, inputStyle: { textAlign: "right", fontSize: "0.75rem", padding: "0 0.75rem" }, defaultValue: row.data.price?.toString(), hideArrows: true, height: "2.5rem", onChange: value => onChangeEquipmentCellValue(rowId, "price", value), min: defaultParams.equipment_list?.[0].price?.min, max: defaultParams.equipment_list?.[0].price?.max, onEnter: () => setEditingEquipmentField(undefined) })) : formatNumber(row.data.price, false);
                                },
                            },
                            {
                                id: "time",
                                title: "Срок использования",
                                subtitle: "лет",
                                width: "10rem",
                                align: "right",
                                onEditCheck: rowId => editingEquipmentField !== undefined && editingEquipmentField[0] === rowId && editingEquipmentField[1] === 3,
                                formatter: (row, rowId) => {
                                    return editingEquipmentField !== undefined && editingEquipmentField[0] === rowId && editingEquipmentField[1] === 3 ? (_jsx(NumberInput, { autofocus: true, style: { border: "none" }, inputStyle: { textAlign: "right", fontSize: "0.75rem", padding: "0 0.75rem" }, defaultValue: row.data.time?.toString(), hideArrows: true, height: "2.5rem", onChange: value => onChangeEquipmentCellValue(rowId, "time", value), min: defaultParams.equipment_list?.[0].time?.min, max: defaultParams.equipment_list?.[0].time?.max, onEnter: () => setEditingEquipmentField(undefined) })) : formatNumber(row.data.time, false);
                                },
                            },
                        ], rows: values.equipment_list.map(({ name, count, price, time, schedule }, rowId) => ({
                            onColumnClick: colIndex => {
                                setEditingEquipmentField(colIndex <= 3 ? [rowId, colIndex] : undefined);
                                setEditingField(undefined);
                            },
                            onMonthClick: colIndex => {
                                const scheduleClone = [...values.equipment_list?.[rowId].schedule];
                                const checkedColsCount = scheduleClone.filter((col, index) => index === colIndex ? col === 0 : col !== 0).length;
                                onUpdateValues({
                                    equipment_list: values.equipment_list.map((row, rIndex) => {
                                        if (rIndex === rowId) {
                                            return {
                                                ...row,
                                                schedule: scheduleClone.map((col, cIndex) => {
                                                    return cIndex === colIndex
                                                        ? col === 0 && checkedColsCount !== 0
                                                            ? (1 / checkedColsCount)
                                                            : 0
                                                        : col !== 0
                                                            ? (1 / checkedColsCount)
                                                            : 0;
                                                }),
                                            };
                                        }
                                        return row;
                                    }),
                                });
                            },
                            onActiveCheck: colIndex => values.equipment_list?.[rowId].schedule?.[colIndex] !== 0 && values.equipment_list?.[rowId].schedule?.[colIndex] !== undefined,
                            onDisabledCheck: colIndex => data.parameters.event_list.find(item => item.type === "power_start").month?.[0] < colIndex + 1,
                            data: {
                                name,
                                count,
                                price,
                                time,
                                months: schedule,
                            },
                        })) })] }), _jsx(FlexSpan, { width: "100%", mt: "1rem", pb: "0.5rem", children: _jsx(GrayButton, { icon: "plus", buttonSize: "s", width: "100%", onClick: onAddEquipment, disabled: values.equipment_list.filter(item => item.id < 0).length > 4, children: "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C \u0441\u0442\u0440\u043E\u043A\u0443" }) })] }));
};
